import { ChevronDown, Folder, X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import {
  ProcessingStatus,
  rowStyles,
} from "../constants/documentParser.constants";
import { UploadTask } from "../interfaces/documentParser.interfaces";

import Avatar from "@/components/displayPrimitives/Avatar";
import { CustomToolTip } from "@/components/ui/CustomToolTip";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { getPreviewImage } from "@/components/uploads/FileUpload";
import { clearAllTask } from "@/redux/slices/uploadSlice";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { cn } from "@/utils/core/generalUtils";

const isFolder = (file: File): string | null => file.webkitRelativePath || null;

interface StatsHeader {
  value: number;
  header: string;
  className?: string;
}

interface StatsHeaderProps {
  data: StatsHeader[];
}

const StatsHeader = ({ data }: StatsHeaderProps): JSX.Element => {
  return (
    <>
      {data.map((item) => (
        <div
          key={item.header}
          className={cn("flex flex-col items-center gap-1.5", item.className)}
        >
          {item.value}
          <span className='text-gray-500 text-xs'>{item.header}</span>
        </div>
      ))}
    </>
  );
};

export const UploadQueueCard = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const queue = useAppSelector((s) => s.uploadQ);
  const [open, setOpen] = React.useState(true);
  const [closed, setClosed] = React.useState(false);

  const folders = new Map<string, UploadTask[]>();

  // Sort queue by createdAt timestamp (most recent first)
  const sortedQueue = [...queue].sort((a, b) => {
    const aCreatedAt = a.createdAt || 0;
    const bCreatedAt = b.createdAt || 0;

    return bCreatedAt - aCreatedAt; // Descending order (most recent first)
  });

  sortedQueue.forEach((t) => {
    const path = isFolder(t.file)
      ? t.file.webkitRelativePath.split("/")[0]
      : "standalone";
    folders.set(path, [...(folders.get(path) || []), t]);
  });

  React.useEffect(() => {
    if (
      queue?.length
      // queue.every((t) => t.status === ProcessingStatus.PENDING)
    ) {
      setOpen(true);
      setClosed(false);
    }

    return (): void => {
      queue.forEach((t) => {
        if (t.file?.type.startsWith("image/")) {
          URL.revokeObjectURL(getPreviewImage({ data: { file: t.file } }));
        }
      });
    };
  }, [queue]);

  const { completed, failed, inProgress } = React.useMemo(() => {
    const completed: typeof queue = [];
    const failed: typeof queue = [];
    const inProgress: typeof queue = [];

    queue.forEach((t) => {
      if (
        t.status === ProcessingStatus.COMPLETED ||
        t.status === ProcessingStatus.FAILED ||
        t.status === ProcessingStatus.DUPLICATE ||
        t.status === ProcessingStatus.UN_SUPPORTED_FORMAT ||
        t.status === ProcessingStatus.LARGE_FILE
      ) {
        (t.status === ProcessingStatus.COMPLETED ? completed : failed).push(t);
      } else {
        inProgress.push(t);
      }
    });

    return { completed, failed, inProgress };
  }, [queue]);

  const isEveryFileUploaded = queue.every(
    (t) =>
      t.status === ProcessingStatus.COMPLETED ||
      t.status === ProcessingStatus.DUPLICATE ||
      t.status === ProcessingStatus.FAILED ||
      t.status === ProcessingStatus.LARGE_FILE ||
      t.status === ProcessingStatus.UN_SUPPORTED_FORMAT,
  );

  const totalFiles = queue.length;
  const totalInProgress = inProgress.length;
  const totalCompleted = completed.length;
  const totalFailed = failed.length;

  const totalProgress = React.useMemo(() => {
    if (totalFiles === 0) {
      return 0;
    }

    const completedFiles = queue.filter(
      (t) =>
        t.status === ProcessingStatus.COMPLETED ||
        t.status === ProcessingStatus.FAILED ||
        t.status === ProcessingStatus.DUPLICATE ||
        t.status === ProcessingStatus.UN_SUPPORTED_FORMAT ||
        t.status === ProcessingStatus.LARGE_FILE,
    ).length;

    return (completedFiles / totalFiles) * 100;
  }, [queue, totalFiles]);

  const completedFilesWithTiming = React.useMemo(() => {
    return queue.filter(
      (t) =>
        t.status === ProcessingStatus.COMPLETED && t.startedAt && t.completedAt,
    );
  }, [queue]);

  const averageProcessingTime = React.useMemo(() => {
    if (completedFilesWithTiming.length === 0) {
      return 0;
    }

    const totalProcessingTime = completedFilesWithTiming.reduce(
      (acc, task) => acc + ((task.completedAt || 0) - (task.startedAt || 0)),
      0,
    );

    return totalProcessingTime / completedFilesWithTiming.length;
  }, [completedFilesWithTiming]);

  const estimatedTimeRemaining = React.useMemo(() => {
    if (totalInProgress === 0) {
      return null;
    }

    const inProgressFiles = queue.filter(
      (t) =>
        t.status !== ProcessingStatus.COMPLETED &&
        t.status !== ProcessingStatus.FAILED &&
        t.status !== ProcessingStatus.DUPLICATE &&
        t.status !== ProcessingStatus.UN_SUPPORTED_FORMAT &&
        t.status !== ProcessingStatus.LARGE_FILE,
    );

    if (inProgressFiles.length === 0) {
      return null;
    }

    // If we have completed files with timing data, use that for estimation
    if (completedFilesWithTiming.length > 0 && averageProcessingTime > 0) {
      // Estimate time based on average processing time per file
      // Since we're now focusing on file completion rather than individual progress,
      // we estimate remaining time as: remaining files × average time per file
      return inProgressFiles.length * averageProcessingTime;
    }

    // Fallback: If no completed files yet, use a rough estimate based on file count
    // Assume average processing time of 30 seconds per file as initial estimate
    const fallbackProcessingTimePerFile = 30000; // 30 seconds in milliseconds

    return inProgressFiles.length * fallbackProcessingTimePerFile;
  }, [
    averageProcessingTime,
    queue,
    totalInProgress,
    completedFilesWithTiming.length,
  ]);

  // Format time remaining into human-readable string
  const formatTimeRemaining = (milliseconds: number | null): string => {
    if (milliseconds === null) {
      return "Calculating...";
    }
    if (milliseconds <= 0) {
      return "Almost done!";
    }
    if (milliseconds < 1000) {
      return "Less than 1s remaining";
    }

    const seconds = Math.ceil(milliseconds / 1000); // Use ceil for more conservative estimates
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h left`;
      }

      return `${hours}h ${remainingMinutes}m left`;
    } else if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      if (remainingSeconds < 10) {
        return `${minutes}m left`;
      }

      return `${minutes}m ${remainingSeconds}s left`;
    } else {
      return `${seconds}s left`;
    }
  };

  if (closed || queue.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-2 right-2 2xl:bottom-6 2xl:right-6 w-[500px] z-50 bg-white shadow-2xl rounded-xl border",
      )}
    >
      {/* header */}
      <section className='space-y-4 px-4 py-5'>
        <div className='flex justify-between mb-2'>
          <span className='text-sm font-semibold'>Over all Progress</span>
          <div className='flex gap-4'>
            <ChevronDown
              className={cn(
                "cursor-pointer size-6 hover:bg-primary hover:text-white rounded-sm transition-opacity duration-300 ease-in-out",
                !open ? "rotate-180" : "rotate-0",
              )}
              onClick={() => setOpen(!open)}
            />
            {isEveryFileUploaded && (
              <X
                className='cursor-pointer size-6 hover:bg-destructive hover:text-white rounded-sm transition-opacity duration-300 ease-in-out'
                onClick={() => {
                  setClosed(true);
                  dispatch(clearAllTask());
                }}
              />
            )}
          </div>
        </div>
        <div className='flex items-center gap-3'>
          <Progress
            segments={[
              {
                value: totalProgress,
              },
            ]}
          />
          <h2 className='text-sm font-semibold'>
            {totalProgress?.toFixed(0)}%
          </h2>
        </div>
        {totalInProgress > 0 && (
          <div className='flex items-center gap-2 text-xs text-gray-500 justify-end -mt-2'>
            <span>{formatTimeRemaining(estimatedTimeRemaining)}</span>
          </div>
        )}
        <div className='flex items-center justify-between px-6'>
          <StatsHeader
            data={[
              {
                header: "Total Files",
                value: totalFiles ?? 0,
              },
              {
                header: "In progress",
                value: totalInProgress ?? 0,
                className: "text-blue-500",
              },
              {
                header: "Completed",
                value: totalCompleted ?? 0,
                className: "text-green-500",
              },
              {
                header: "Errors",
                value: totalFailed ?? 0,
                className: "text-destructive",
              },
            ]}
          />
        </div>
      </section>

      {open && (
        <>
          <Separator />
          <div className='space-y-3 max-h-72 overflow-y-auto flex flex-col overflow-x-clip px-2 py-3 w-full max-w-full'>
            {Array.from(folders.entries()).map(([folderName, tasks]) => (
              <div key={folderName} className='space-y-1'>
                {/* folder header */}
                {folderName !== "standalone" && (
                  <div className='flex items-center gap-2 text-xs font-medium'>
                    <Folder className='text-blue-500' />
                    <span>{folderName}</span>
                    <span className='text-gray-400'>
                      •{" "}
                      {
                        tasks.filter(
                          (t) => t.status === ProcessingStatus.COMPLETED,
                        ).length
                      }
                      /{tasks.length}
                    </span>
                  </div>
                )}

                {/* files */}
                {tasks?.map((t) => (
                  <div
                    key={t.id}
                    className={cn(
                      "gap-2 px-1 w-full overflow-clip flex items-center",
                    )}
                  >
                    <Avatar
                      name={t.retryPreview?.fileName || "Name"}
                      src={
                        t?.retryPreview?.previewURL ??
                        getPreviewImage({
                          data: {
                            file: t.file,
                            ...(t.file?.type.startsWith("image/") && {
                              dataURL: URL.createObjectURL(t.file),
                            }),
                          },
                        })
                      }
                      size={27}
                    />
                    <div
                      className={cn(
                        "justify-between w-[90%] flex",
                        t.status === ProcessingStatus.PENDING &&
                          "text-gray-300",
                      )}
                    >
                      <CustomToolTip
                        toolTipContent={t.file.name}
                        toolTipTrigger={
                          <div className='truncate text-sm w-1/2'>
                            {t.file.name}
                          </div>
                        }
                      />
                      <div
                        className={cn(
                          "text-xs  w-fit px-1 py-0.5 rounded-sm text-pretty tracking-wide",
                          rowStyles[t.status]?.bg || "bg-gray-100",
                          rowStyles[t.status]?.color || "text-gray-700",
                        )}
                      >
                        {rowStyles[t.status]?.label || "Requeued Extract"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const UploadQueueOverlay = (): React.ReactPortal =>
  createPortal(<UploadQueueCard />, document.body);
